import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, CheckCircle, Snowflake, WashingMachine, Utensils, Flame } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { BrandsSlider } from '../components/BrandsSlider';
import { Testimonials } from '../components/Testimonials';

const torontoSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Infinity Appliances Repair - Toronto",
  "telephone": "+1-647-919-6893",
  "priceRange": "$$",
  "address": { "@type": "PostalAddress", "addressLocality": "Toronto", "addressRegion": "ON", "addressCountry": "CA" },
  "areaServed": { "@type": "City", "name": "Toronto" },
  "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "150" }
};

const services = [
  { icon: Snowflake, title: 'Refrigerator Repair', description: 'Expert fridge diagnostics and repair' },
  { icon: WashingMachine, title: 'Washer & Dryer', description: 'Same-day laundry appliance service' },
  { icon: Utensils, title: 'Dishwasher Repair', description: 'Fix leaks and drainage issues' },
  { icon: Flame, title: 'Stove & Oven', description: 'Get cooking again fast' },
];

export const TorontoPage = () => {
  useEffect(() => {
    document.title = 'Appliance Repair Toronto | Same-Day Service | Infinity';
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) { metaDesc = document.createElement('meta'); metaDesc.name = 'description'; document.head.appendChild(metaDesc); }
    metaDesc.content = 'Fast same-day appliance repair in Downtown Toronto. Expert technicians for fridges, washers, dryers & stoves. 2-hour arrival. Call 647-919-6893.';
    let schemaScript = document.querySelector('#toronto-schema');
    if (!schemaScript) { schemaScript = document.createElement('script'); schemaScript.id = 'toronto-schema'; schemaScript.type = 'application/ld+json'; document.head.appendChild(schemaScript); }
    schemaScript.textContent = JSON.stringify(torontoSchema);
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="toronto-page">
      <section className="hero-overlay relative min-h-[70vh] flex items-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1517090504586-fde19ea6066f?crop=entropy&cs=srgb&fm=jpg&q=85)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="hero-content max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <nav className="mb-6 text-white/70 text-sm"><Link to="/" className="hover:text-white">Home</Link><span className="mx-2">/</span><span className="text-white">Toronto</span></nav>
            <div className="trust-badge mb-8"><MapPin size={18} className="text-[#FF6B00]" /><span className="text-white font-semibold">Serving Downtown Toronto & GTA</span></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>Fast Same-Day Appliance Repair in Downtown Toronto & GTA</h1>
            <p className="text-lg text-white/80 mb-8 leading-relaxed max-w-2xl">From high-rise condos in the Entertainment District to family homes in the Beaches, Infinity Appliances Repair provides the fastest 2-hour response time in Toronto.</p>
            <a href="tel:647-919-6893" className="pulse-cta inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-lg text-white transition-transform hover:scale-105 shadow-xl" style={{ background: '#FF6B00', fontFamily: 'Montserrat, sans-serif' }} data-testid="toronto-hero-cta"><Phone size={22} /><span>Call 647-919-6893</span></a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif', color: '#1A2B4C' }}>Toronto's Trusted Appliance Repair Experts</h2>
              <p className="text-[#475569] text-lg leading-relaxed mb-6">Whether you're in a downtown condo near the CN Tower or a townhouse in Leslieville, our Toronto technicians provide rapid response times across the city. We understand that in Canada's busiest city, time is everything.</p>
              <p className="text-[#475569] leading-relaxed mb-8">Our licensed technicians serve all Toronto neighborhoods including the Financial District, Yorkville, Queen West, the Annex, and Scarborough. We're experts in all major brands and provide same-day service for most repairs.</p>
              <div className="space-y-4 mb-8">
                {['2-Hour Arrival Windows in Toronto', 'Licensed & Insured Technicians', 'Condo & High-Rise Specialists', 'All Major Brands Serviced', 'No Hidden Fees'].map((feature) => (
                  <div key={feature} className="flex items-center gap-3"><CheckCircle size={20} style={{ color: '#FF6B00' }} /><span className="font-medium" style={{ color: '#1A2B4C' }}>{feature}</span></div>
                ))}
              </div>
              <a href="tel:647-919-6893" className="inline-flex items-center gap-3 px-6 py-3 rounded-full font-bold text-white transition-transform hover:scale-105" style={{ background: '#FF6B00', fontFamily: 'Montserrat, sans-serif' }} data-testid="toronto-content-cta"><Phone size={18} /><span>Call for Toronto Same-Day Service</span></a>
            </div>
            <div>
              <div className="map-container rounded-2xl overflow-hidden shadow-xl">
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d184552.67411781!2d-79.5181!3d43.7182!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89d4cb90d7c63ba5%3A0x323555502ab4c477!2sToronto%2C%20ON!5e0!3m2!1sen!2sca!4v1710000000010" width="100%" height="400" style={{ border: 0 }} allowFullScreen="" loading="lazy" title="Toronto Service Area Map" data-testid="toronto-map"></iframe>
              </div>
              <p className="text-center text-[#475569] mt-4 text-sm">Serving all Toronto neighborhoods including Downtown, Midtown, East End & West End</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F8F9FC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-center mb-10" style={{ fontFamily: 'Montserrat, sans-serif', color: '#1A2B4C' }}>Our Toronto Repair Services</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => { const IconComponent = service.icon; return (
              <Card key={service.title} className="glass-card border-0 shadow-lg hover:shadow-xl transition-shadow" data-testid={`toronto-service-${service.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(255, 107, 0, 0.1)' }}><IconComponent size={28} style={{ color: '#FF6B00' }} /></div>
                  <h3 className="font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif', color: '#1A2B4C' }}>{service.title}</h3>
                  <p className="text-sm text-[#475569]">{service.description}</p>
                </CardContent>
              </Card>
            ); })}
          </div>
        </div>
      </section>

      <BrandsSlider />
      <Testimonials />

      <section className="py-16" style={{ background: '#1A2B4C' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>Need Appliance Repair in Toronto?</h2>
          <p className="text-white/70 mb-8 text-lg">Our Toronto technicians are ready to help. Same-day service available across the city.</p>
          <a href="tel:647-919-6893" className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-lg text-white transition-transform hover:scale-105" style={{ background: '#FF6B00', fontFamily: 'Montserrat, sans-serif' }} data-testid="toronto-final-cta"><Phone size={22} /><span>Call 647-919-6893 Now</span></a>
        </div>
      </section>
    </main>
  );
};

export default TorontoPage;
